import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PaymentIcon from "@mui/icons-material/Payment";
import { IconButton, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { type WorkerStripeAccount } from "../api/useGetWorkerStripeAccount";

interface WorkerStripeActionItemProps {
  stripeAccount: WorkerStripeAccount;
  selected: boolean;
  copied?: boolean;
  onClick: () => void;
}

export const WorkerStripeActionItem = forwardRef<HTMLDivElement, WorkerStripeActionItemProps>(
  (props, ref) => {
    const { stripeAccount, selected, copied: copiedFromKeyboard = false, onClick } = props;
    const [copiedFromClick, setCopiedFromClick] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
      () => () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
      },
      []
    );

    const isCopied = copiedFromKeyboard || copiedFromClick;

    const handleCopy = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        navigator.clipboard.writeText(stripeAccount.accountId).then(
          () => {
            setCopiedFromClick(true);
            timerRef.current = setTimeout(() => {
              setCopiedFromClick(false);
            }, 2000);
          },
          () => {
            showErrorToast("Failed to copy to clipboard");
          }
        );
      },
      [stripeAccount.accountId]
    );

    return (
      <ListItemButton
        ref={ref}
        selected={selected}
        sx={{
          paddingY: 0.75,
          paddingLeft: 6,
          paddingRight: 2,
          "&.Mui-selected": {
            backgroundColor: "action.selected",
          },
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <PaymentIcon color="success" />
        </ListItemIcon>
        <ListItemText
          primary={`Go to Stripe Account (${stripeAccount.accountId})`}
          primaryTypographyProps={{ variant: "body2" }}
        />
        <Tooltip title={isCopied ? "Copied!" : "Copy Stripe Account ID"}>
          <IconButton size="small" color={isCopied ? "success" : "default"} onClick={handleCopy}>
            {isCopied ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </ListItemButton>
    );
  }
);

WorkerStripeActionItem.displayName = "WorkerStripeActionItem";
